type Metrics = {
  views?: number
  likes?: number
  comments?: number
  shares?: number
  reach?: number
  saves?: number
  fetched_at?: string
}

function formatCompact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}m`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`
  return String(n)
}

export default function SocialPostMetrics({
  metrics,
}: {
  metrics?: Metrics | Record<string, unknown>
}) {
  if (!metrics || typeof metrics !== "object") return null

  const m = metrics as Metrics
  const hasAny =
    (m.views ?? 0) > 0 ||
    (m.likes ?? 0) > 0 ||
    (m.comments ?? 0) > 0 ||
    (m.shares ?? 0) > 0

  if (!hasAny) {
    return (
      <p className="mt-2 text-[10px] text-warm-grey/40">
        Metrics not yet available
      </p>
    )
  }

  const chips = [
    { icon: EyeIcon, label: "views", value: m.views },
    { icon: HeartIcon, label: "likes", value: m.likes },
    { icon: MessageIcon, label: "comments", value: m.comments },
    { icon: ShareIcon, label: "shares", value: m.shares },
  ].filter((c) => (c.value ?? 0) > 0)

  return (
    <div className="mt-2 flex flex-wrap items-center gap-3">
      {chips.map((chip) => {
        const Icon = chip.icon
        return (
          <span
            key={chip.label}
            className="flex items-center gap-1 text-[11px] text-warm-grey"
            title={`${chip.value?.toLocaleString()} ${chip.label}`}
          >
            <Icon className="size-3 text-warm-grey/50" />
            {formatCompact(chip.value!)} {chip.label}
          </span>
        )
      })}
      {m.fetched_at && (
        <span className="text-[10px] text-warm-grey/30" title={`Last updated ${m.fetched_at}`}>
          Updated {timeAgo(m.fetched_at)}
        </span>
      )}
    </div>
  )
}

function timeAgo(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime()
  const hours = Math.floor(diff / 3_600_000)
  if (hours < 1) return "just now"
  if (hours === 1) return "1h ago"
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days === 1) return "1d ago"
  return `${days}d ago`
}

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
    </svg>
  )
}

function HeartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12Z" />
    </svg>
  )
}

function MessageIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
    </svg>
  )
}

function ShareIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z" />
    </svg>
  )
}
